/**
 * English - `signature`: the signature settings screen - the signature block
 * on invoices and the sign-off added to emails. Tooltips live in
 * `tipsSignature`. Keys are listed in `features/signature/copy.ts`.
 * Conventions: see `common.ts`.
 */
const signature: Record<string, string> = {
  title: 'Signature',
  intro:
    'Sign your invoices and emails. The invoice signature is printed above "Authorised Signatory"; the email signature is added to the emails your shop sends customers.',

  'tabs.label': 'Signature settings',
  'tabs.invoice': 'Invoice',
  'tabs.email': 'Email',
  loading: 'Loading your signature…',
  load_failed: 'Could not load your signature.',
  save: 'Save',
  saving: 'Saving…',
  saved: 'Signature saved.',
  save_failed: 'Could not save your signature.',
  unsaved: 'You have unsaved changes.',
  discard: 'Discard changes',
  read_only: 'You can view the signature, but only someone who can edit business settings can change it.',

  'invoice.title': 'Signature on invoices',
  'invoice.description':
    'Shown at the bottom right of every new invoice. Invoices you have already issued keep the signature they were issued with.',
  'invoice.enabled': 'Show signature on invoices',
  'invoice.image': 'Signature image',
  'invoice.image_alt': 'Your signature',
  'invoice.image_none': 'No signature image yet',
  'invoice.upload': 'Upload image',
  'invoice.draw': 'Draw it',
  'invoice.remove_image': 'Remove image',
  'invoice.uploading': 'Uploading your signature…',
  'invoice.upload_failed': 'Could not upload that image.',
  'invoice.upload_type': 'Choose a PNG, JPG or WebP image.',
  'invoice.upload_too_large': 'That image is too large. Try a smaller file.',
  'invoice.signatory_name': 'Signatory name',
  'invoice.signatory_name_placeholder': 'e.g. Asha Rao',
  'invoice.designation': 'Designation',
  'invoice.designation_placeholder': 'e.g. Proprietor',
  'invoice.preview_title': 'How it looks on an invoice',
  'invoice.preview_off': 'Switched off: invoices show only the plain signature block, as above.',
  'invoice.preview_pdf': 'Preview PDF',
  'invoice.preview_pdf_loading': 'Preparing preview…',
  'invoice.preview_pdf_failed': 'Could not prepare the preview.',
  'invoice.business_fallback': 'Your business',

  'pad.label': 'Signature drawing area',
  'pad.hint': 'Sign inside the box with your finger, a stylus or the mouse.',
  'pad.clear': 'Clear',
  'pad.use': 'Use this signature',
  'pad.cancel': 'Cancel',

  'email.title': 'Signature on emails',
  'email.description':
    'Added to the order and customer emails your shop sends. Changes apply to the next email that goes out.',
  'email.enabled': 'Add signature to emails',
  'email.sign_off': 'Sign-off',
  'email.sign_off_placeholder': 'e.g. Warm regards,',
  'email.sender_name': 'Sender name',
  'email.designation': 'Designation or team',
  'email.designation_placeholder': 'e.g. Customer care',
  'email.show_image': 'Show signature image',
  'email.show_image_needs_image': 'Add a signature image on the Invoice tab first.',
  'email.contact_title': 'Contact details',
  'email.show_phone': 'Show phone number',
  'email.phone': 'Phone number',
  'email.show_email': 'Show email address',
  'email.contact_email': 'Email address',
  'email.show_website': 'Show website',
  'email.website': 'Website',
  'email.override_hint': 'Leave blank to use your business profile.',
  'email.phone_placeholder': 'e.g. +91 98765 43210',
  'email.contact_email_placeholder': 'e.g. hello@yourshop.in',
  'email.website_placeholder': 'e.g. https://yourshop.in',
  'email.placement_tip':
    'Tip: put {{signature}} in an email template to choose where the signature goes. Otherwise it is added automatically at the end of the email.',
  'email.preview_title': 'Email preview',
  'email.preview_loading': 'Preparing preview…',
  'email.preview_failed': 'Could not prepare the preview.',
  'email.preview_subject': 'Subject:',
  'email.preview_frame': 'Sample email with your signature',
  'email.preview_off': 'Switched off: emails are sent without a signature.',
  'email.test': 'Send test email',
  'email.test_sending': 'Sending…',
  'email.test_sent': 'Test email sent to {email}.',
  'email.test_failed': 'Could not send the test email.',
  'email.test_not_configured': 'Email sending is not set up yet, so the test email could not be sent.',
  'email.test_send_failed': 'The test email could not be delivered. Please try again in a few minutes.',
  'email.test_rate_limited': 'Too many test emails. Please wait a little before sending another.',

  'errors.too_long': 'Use at most {max} characters.',
  'errors.website': 'Enter a full address starting with https://',
  'errors.email': 'Enter a valid email address.',
  'errors.image_not_uploaded': 'That image is not one of your uploads. Upload or draw the signature again.',
};

export default signature;
